import * as React from 'react'

import type moment from 'moment'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import useDefaultAttributesFromRows from 'modules/shift_management/hooks/useDefaultAttributesFromRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import CommonSelectorsForm, {
  TIME_PERIODS,
} from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import { Leave } from 'modules/shift_management/lib/types'

import { getWeekDay } from './useDateRangeSelection'
import useDefaultValues from './useDefaultValues'

export interface Props {
  date?: moment.Moment
  leave?: Leave
  contractVersions: ContractVersions
}

const useCommonSelectors = ({ date, leave, contractVersions }: Props) => {
  const [{ view }] = useSchedule()
  const { daysByRow } = useSelectionState()

  // When daysByRow are from work area view, they have work area ids and not employee ids
  // so we need to start with an empty array for the employee selector to work correctly
  const selectedEmployeesIds = Object.keys(daysByRow)
  const getIds = selectedEmployeesIds.map((id) => Number(id))
  const employeeIds = !!selectedEmployeesIds.length && view.isByEmployees ? getIds : []

  const { loading: loadingEmployees, selectableEmployees } = useSelectableEmployees(
    view.startAt,
    view.endAt,
    contractVersions
  )
  const defaultAttributes = useDefaultAttributesFromRows()

  const [selectedEmployees, setSelectedEmployees] = React.useState<number[]>(employeeIds)

  const commonSelectorsForm = useLazyRef<CommonSelectorsForm>(
    () =>
      new CommonSelectorsForm({
        start_date: leave
          ? dateToString(leave._startOn, 'backend')
          : dateToString(view.firstDay, 'backend'),
        end_date:
          leave && leave._finishOn
            ? dateToString(leave._finishOn, 'backend')
            : dateToString(view.lastDay, 'backend'),
        apply_on_days: view.isDaily ? getWeekDay(view.startAt) : getWeekDay(view.currentDateString),
        time_period: leave ? TIME_PERIODS.DAILY : TIME_PERIODS.DOES_NOT_REPEAT,
        employee_id: selectedEmployees.length ? selectedEmployees[0] : undefined,
      })
  ).current
  const { weekDays, daysByEmployee } = useDefaultValues(
    selectedEmployees,
    commonSelectorsForm,
    date,
    leave
  )

  return {
    commonSelectorsForm,
    defaultAttributes,
    loadingEmployees,
    selectedEmployees,
    setSelectedEmployees,
    selectableEmployees,
    weekDays,
    daysByEmployee,
  }
}

export default useCommonSelectors
